// Recruitment Focus — samostatný nástroj fáze Analyze (CRUD + AI Recruiter + uložené seznamy).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Brain, Trash, PencilSimple, Globe, Binoculars } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import ContextArticles from "../components/ContextArticles";
import { useAuth } from "../contexts/AuthContext";
import { listFocuses, createFocus, patchFocus, archiveFocus, FOCUS_TYPES, FOCUS_STATUS_LABELS } from "../lib/lab-api";
import RecruiterPanel from "../components/lab/RecruiterPanel";
import FocusCandidates from "../components/lab/FocusCandidates";
import SavedRecruiterLists from "../components/lab/SavedRecruiterLists";
import PublishFocusList from "../components/lab/PublishFocusList";
import FocusListsAdminSection from "../components/lab/FocusListsAdminSection";
import { focusListFromFocus } from "../lib/focus-api";

const EMPTY_FOCUS = { title: "", type: "long_term_starter", position_filters: [], budget_max: "", gw_start: 1, gw_end: 6, risk_level: "medium", requirements: "", note: "" };
const inputCls = "bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-full focus:outline-none focus:border-fa-mint placeholder:text-white/25";

function FocusForm({ initial, onDone, onCancel }) {
    const [f, setF] = useState({ ...EMPTY_FOCUS, ...(initial || {}) });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const togglePos = (p) => setF({ ...f, position_filters: f.position_filters.includes(p) ? f.position_filters.filter((x) => x !== p) : [...f.position_filters, p] });

    const save = async () => {
        if (!f.title || f.title.length < 3) { toast.error("Zadej název focusu (min. 3 znaky)"); return; }
        setBusy(true);
        try {
            const payload = { title: f.title, type: f.type, position_filters: f.position_filters,
                budget_max: f.budget_max === "" ? null : Number(f.budget_max),
                gw_start: Number(f.gw_start), gw_end: Number(f.gw_end),
                risk_level: f.risk_level, requirements: f.requirements || null, note: f.note || null };
            if (initial?.id) await patchFocus(initial.id, payload);
            else await createFocus(payload);
            toast.success(initial?.id ? "Focus upraven" : "Recruitment Focus vytvořen");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Uložení selhalo");
        } finally { setBusy(false); }
    };

    return (
        <div className="border border-fa-mint/30 bg-black/50 p-4 space-y-3" data-testid="focus-form">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-mint">
                {initial?.id ? "Upravit focus" : "Nový Recruitment Focus"}
            </div>
            <div className="grid md:grid-cols-4 gap-3">
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block md:col-span-2">
                    <div>Název *</div>
                    <input value={f.title} onChange={set("title")} placeholder="Náhrada za Robertsona" className={inputCls} data-testid="focus-f-title" />
                </label>
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                    <div>Typ hledání *</div>
                    <select value={f.type} onChange={set("type")} className={inputCls} data-testid="focus-f-type">
                        {FOCUS_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                </label>
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                    <div>Max cena (£)</div>
                    <input type="number" step="0.5" value={f.budget_max} onChange={set("budget_max")} placeholder="—" className={inputCls} data-testid="focus-f-budget" />
                </label>
                <div className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5">
                    <div>Pozice</div>
                    <div className="flex gap-1">
                        {["GKP", "DEF", "MID", "FWD"].map((p) => (
                            <button key={p} type="button" onClick={() => togglePos(p)} data-testid={`focus-pos-${p}`}
                                    className={`px-2 py-1.5 font-mono text-[10px] border ${f.position_filters.includes(p) ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                    <div>Období GW od–do *</div>
                    <div className="flex gap-1">
                        <input type="number" min={1} max={38} value={f.gw_start} onChange={set("gw_start")} className={inputCls} data-testid="focus-f-gw-start" />
                        <input type="number" min={1} max={38} value={f.gw_end} onChange={set("gw_end")} className={inputCls} data-testid="focus-f-gw-end" />
                    </div>
                </label>
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block">
                    <div>Tolerance rizika</div>
                    <select value={f.risk_level} onChange={set("risk_level")} className={inputCls} data-testid="focus-f-risk">
                        <option value="low">Nízká</option>
                        <option value="medium">Střední</option>
                        <option value="high">Vysoká</option>
                    </select>
                </label>
                <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 space-y-0.5 block md:col-span-2">
                    <div>Požadavky / poznámka</div>
                    <input value={f.requirements || ""} onChange={set("requirements")} placeholder="jistota minut, standardky…" className={inputCls} data-testid="focus-f-requirements" />
                </label>
            </div>
            <div className="flex gap-2">
                <button type="button" onClick={save} disabled={busy} data-testid="focus-save-btn"
                        className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold disabled:opacity-40">
                    {busy ? "Ukládám…" : "Uložit focus"}
                </button>
                <button type="button" onClick={onCancel} className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60">
                    Zrušit
                </button>
            </div>
        </div>
    );
}

export default function RecruitmentFocus() {
    const { user, login } = useAuth();
    const [focuses, setFocuses] = useState([]);
    const [editing, setEditing] = useState(null);
    const [recruiting, setRecruiting] = useState(null);
    const [candidatesFor, setCandidatesFor] = useState(null);
    const [pubList, setPubList] = useState(null);
    const [pubRefresh, setPubRefresh] = useState(0);
    const [pubBusyId, setPubBusyId] = useState(null);

    const makePublicList = async (f) => {
        if (pubBusyId) return;
        setPubBusyId(f.id);
        try {
            const doc = await focusListFromFocus(f.id);
            setPubList(doc);
            setPubRefresh((x) => x + 1);
        } catch (e) { toast.error(e.response?.data?.detail || "Vytvoření seznamu selhalo — zkus to znovu"); }
        finally { setPubBusyId(null); }
    };

    const load = useCallback(() => {
        if (!user) return;
        listFocuses().then((d) => setFocuses(d.items || [])).catch(() => {});
    }, [user]);
    useEffect(() => { load(); }, [load]);

    const remove = async (f) => {
        try { await archiveFocus(f.id); toast.success("Focus archivován"); load(); }
        catch { toast.error("Archivace selhala"); }
    };

    return (
        <AppShell>
            <div className="space-y-6" data-testid="recruitment-focus-page">
                <header className="border-2 border-fa-mint/40 bg-gradient-to-br from-fa-mint/10 via-transparent to-fa-sky/5 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Brain size={11} weight="fill" /> Manager Lab · Analyze
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Recruitment Focus</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Koho hledám, proč ho hledám a pro jaké období? Vytvoř focus a nech <strong className="text-fa-mint">AI Recruitera</strong> doporučit kandidáty — s fit skóre, výhodami i riziky. Focus můžeš vystavit komunitě ve Scout Hubu.
                    </p>
                </header>

                <section className="space-y-3">
                    <div className="flex items-center justify-end">
                        {user && editing === null && (
                            <button type="button" onClick={() => setEditing({})} data-testid="focus-create-btn"
                                    className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold flex items-center gap-1.5">
                                <Plus size={12} weight="bold" /> Vytvořit Recruitment Focus
                            </button>
                        )}
                    </div>
                    {!user && (
                        <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/60" data-testid="analyze-login-cta">
                            Pro vytváření Recruitment Focusů se{" "}
                            <button type="button" onClick={login} className="text-fa-mint underline">přihlas</button>.
                        </div>
                    )}
                    {editing !== null && (
                        <FocusForm initial={editing.id ? editing : null}
                                   onDone={() => { setEditing(null); load(); }}
                                   onCancel={() => setEditing(null)} />
                    )}
                    {user && focuses.length > 0 && (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="focus-list">
                            {focuses.map((f) => (
                                <div key={f.id} className="border border-white/10 bg-black/40 p-4 space-y-2" data-testid={`focus-card-${f.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-display font-bold text-sm">{f.title}</div>
                                        <div className="flex gap-1.5 flex-shrink-0">
                                            <button type="button" onClick={() => setEditing(f)} className="text-fa-sky" data-testid={`focus-edit-${f.id}`}><PencilSimple size={13} /></button>
                                            <button type="button" onClick={() => remove(f)} className="text-white/30 hover:text-red-400" data-testid={`focus-delete-${f.id}`}><Trash size={13} /></button>
                                        </div>
                                    </div>
                                    <div className="font-mono text-[9px] uppercase text-white/45">
                                        {FOCUS_TYPES.find((t) => t.key === f.type)?.label || f.type}
                                        {f.position_filters?.length ? ` · ${f.position_filters.join("/")}` : ""}
                                        {f.budget_max != null ? ` · do £${f.budget_max}` : ""}
                                    </div>
                                    <div className="flex items-center gap-2 font-mono text-[9px]">
                                        <span className="text-white/60">GW{f.gw_start}–{f.gw_end}</span>
                                        <span className="px-1.5 py-0.5 border border-fa-sky/40 text-fa-sky uppercase">{FOCUS_STATUS_LABELS[f.status] || f.status}</span>
                                    </div>
                                    <button type="button" onClick={() => setCandidatesFor(f)} data-testid={`focus-candidates-${f.id}`}
                                            className="w-full font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-sky/50 text-fa-sky hover:bg-fa-sky hover:text-black transition-colors flex items-center justify-center gap-1.5">
                                        <Binoculars size={11} /> Kandidáti{f.candidate_count ? ` (${f.candidate_count}/11)` : " (0/11)"}
                                    </button>
                                    <button type="button" onClick={() => setRecruiting(f)} data-testid={`focus-recruit-${f.id}`}
                                            className="w-full font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black transition-colors flex items-center justify-center gap-1.5">
                                        <Brain size={11} /> AI Recruiter{f.recommended_count ? ` (${f.recommended_count})` : ""}
                                    </button>
                                    {user?.is_admin && (
                                        <button type="button" onClick={() => makePublicList(f)} disabled={pubBusyId != null} data-testid={`focus-make-public-list-${f.id}`}
                                                className="w-full font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-yellow/50 text-fa-yellow hover:bg-fa-yellow hover:text-black transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50">
                                            <Globe size={11} /> {pubBusyId === f.id ? "Připravuji seznam… (~30 s)" : "Fokus seznam pro čtenáře"}
                                        </button>
                                    )}
                                    <button type="button"
                                            onClick={async () => {
                                                try {
                                                    await patchFocus(f.id, { is_public: !f.is_public });
                                                    toast.success(f.is_public ? "Focus skryt" : "Focus vystaven komunitě — najdeš ho ve Scout Hubu");
                                                    load();
                                                } catch { toast.error("Změna selhala"); }
                                            }}
                                            data-testid={`focus-public-${f.id}`}
                                            className={`w-full font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center justify-center gap-1.5 ${f.is_public ? "border-fa-sky bg-fa-sky/10 text-fa-sky" : "border-white/15 text-white/45 hover:text-fa-sky hover:border-fa-sky/40"}`}>
                                        <Globe size={11} /> {f.is_public ? "Veřejný — komunita scoutuje" : "Vystavit komunitě"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {candidatesFor && (
                        <FocusCandidates focus={candidatesFor} onClose={() => { setCandidatesFor(null); load(); }} onChanged={load} />
                    )}
                    {recruiting && (
                        <RecruiterPanel focus={recruiting} onClose={() => { setRecruiting(null); load(); }} onPoolChange={load} />
                    )}
                    {user && focuses.length === 0 && editing === null && (
                        <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/50" data-testid="focus-empty">
                            Zatím žádný focus — vytvoř první tlačítkem výše.
                        </div>
                    )}
                    {user?.is_admin && <FocusListsAdminSection refresh={pubRefresh} onOpen={setPubList} />}
                    {pubList && <PublishFocusList list={pubList} onClose={() => { setPubList(null); setPubRefresh((x) => x + 1); }} />}
                    {user && <SavedRecruiterLists onPoolChange={load} />}
                </section>
                <ContextArticles category="players" />
            </div>
        </AppShell>
    );
}
